
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { UserForm } from './UserForm';
import { UserList } from './user-management/UserList';
import { useUserData, ProfileWithOrganization } from './user-management/useUserData';
import { User, Plus } from 'lucide-react';
import { Profile } from '@/types/auth';

export function UserManagement() {
  const { profiles, organizations, loading, fetchData } = useUserData();
  const [selectedUser, setSelectedUser] = useState<ProfileWithOrganization | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleUserSubmit = () => {
    setIsDialogOpen(false);
    setSelectedUser(null);
    fetchData();
  };

  const handleEdit = (profile: ProfileWithOrganization) => {
    setSelectedUser(profile);
    setIsDialogOpen(true);
  };

  const handleDialogChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) setSelectedUser(null);
  };

  // Convert ProfileWithOrganization to Profile for UserForm compatibility
  const convertToProfile = (profile: ProfileWithOrganization | null): Profile | null => {
    if (!profile) return null;
    
    return {
      id: profile.id,
      first_name: profile.first_name,
      last_name: profile.last_name,
      role: profile.role,
      organization_id: profile.organization_id,
      gender: profile.gender,
      contact_number1: profile.contact_number1,
      contact_number2: profile.contact_number2,
      is_active: profile.is_active,
    };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>Loading users...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <User className="h-5 w-5" />
            User Management
          </span>
          <Dialog open={isDialogOpen && !selectedUser} onOpenChange={(open) => {
            if (!selectedUser) {
              setIsDialogOpen(open);
            }
          }}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setSelectedUser(null);
                setIsDialogOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <UserForm 
                user={null} 
                organizations={organizations}
                onSubmit={handleUserSubmit}
              />
            </DialogContent>
          </Dialog>
        </CardTitle>
        <CardDescription>
          Manage users and their roles
        </CardDescription>
      </CardHeader>
      <CardContent>
        <UserList
          profiles={profiles}
          organizations={organizations}
          isDialogOpen={isDialogOpen}
          selectedUserId={selectedUser?.id || null}
          onEdit={handleEdit}
          onDialogChange={handleDialogChange}
          onSubmit={handleUserSubmit}
        />
      </CardContent>
    </Card>
  );
}
